<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();

const globalConfig = window.globalConfig || {};
const isDark = ref(document.body.classList.contains('dark'));

const installationName = computed(
  () =>
    globalConfig.INSTALLATION_NAME ||
    'Atendimento Comercial | Mayara Bioni'
);

const logoThumbnail = computed(() => globalConfig.LOGO_THUMBNAIL || '');

const inboxes = computed(() => store.getters['inboxes/getAllInboxes'] || []);

const normalizeChannelType = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

const findInbox = type =>
  inboxes.value.find(inbox => {
    const channelType = normalizeChannelType(
      inbox.channelType || inbox.channel_type
    );

    if (type === 'whatsapp') return channelType.includes('whatsapp');
    if (type === 'instagram') return channelType.includes('instagram');
    if (type === 'email') return channelType.includes('email');

    return false;
  });

const whatsappInbox = computed(() => findInbox('whatsapp'));
const instagramInbox = computed(() => findInbox('instagram'));
const emailInbox = computed(() => findInbox('email'));

const goToConversations = () => {
  router.push(`/app/accounts/${accountId.value}/dashboard`);
};

const goToSearch = () => {
  router.push(`/app/accounts/${accountId.value}/search`);
};

const goToPatients = () => {
  router.push({
    name: 'contacts_dashboard_index',
    params: {
      accountId: accountId.value,
    },
    query: {
      page: 1,
    },
  });
};

const goToCommercial = () => {
  router.push({
    name: 'mayara_commercial',
    params: {
      accountId: accountId.value,
    },
  });
};

const goToAdministration = () => {
  router.push({
    name: 'settings_home',
    params: {
      accountId: accountId.value,
    },
  });
};

const goToMyAccount = () => {
  router.push({
    name: 'profile_settings_index',
    params: {
      accountId: accountId.value,
    },
  });
};

const goToInbox = inbox => {
  if (!inbox?.id) return;
  router.push(`/app/accounts/${accountId.value}/inbox/${inbox.id}`);
};

const activeInboxId = computed(() =>
  Number(route.params.inboxId || route.params.inbox_id || 0)
);

const isAdministrationArea = computed(
  () =>
    route.name === 'settings_home' ||
    route.path.includes(`/app/accounts/${accountId.value}/settings`)
);

const isMyAccountArea = computed(
  () =>
    route.name === 'profile_settings' ||
    route.path.includes(`/app/accounts/${accountId.value}/profile`)
);

const isSearchArea = computed(
  () => route.path.includes(`/app/accounts/${accountId.value}/search`)
);

const isPatientsArea = computed(() =>
  route.path.includes(`/app/accounts/${accountId.value}/contacts`)
);

const isCommercialArea = computed(() =>
  route.path.includes(`/app/accounts/${accountId.value}/commercial`)
);

const toggleTheme = () => {
  const nextTheme = isDark.value ? 'light' : 'dark';

  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, nextTheme);

  setColorTheme(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  isDark.value = document.body.classList.contains('dark');
};

const channels = computed(() => [
  {
    key: 'all',
    name: 'Todos os canais',
    description: 'Visão unificada',
    icon: 'i-lucide-inbox',
    inbox: null,
    available: true,
  },
  {
    key: 'whatsapp',
    name: 'WhatsApp',
    description: 'Conversas pelo WhatsApp',
    icon: 'i-ph-whatsapp-logo-fill',
    inbox: whatsappInbox.value,
    available: !!whatsappInbox.value,
  },
  {
    key: 'instagram',
    name: 'Instagram',
    description: 'Direct e mensagens',
    icon: 'i-lucide-instagram',
    inbox: instagramInbox.value,
    available: !!instagramInbox.value,
  },
  {
    key: 'email',
    name: 'E-mail',
    description: 'Atendimento por e-mail',
    icon: 'i-lucide-mail',
    inbox: emailInbox.value,
    available: !!emailInbox.value,
  },
]);

const isChannelActive = channel => {
  if (channel.key === 'all') return !activeInboxId.value;
  return Number(channel.inbox?.id) === activeInboxId.value;
};

const openChannel = channel => {
  if (channel.key === 'all') {
    goToConversations();
    return;
  }

  goToInbox(channel.inbox);
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
  isDark.value = document.body.classList.contains('dark');
});
</script>

<template>
  <aside
    class="relative flex flex-col w-[270px] min-w-[270px] h-full overflow-hidden border-r border-n-weak bg-n-background"
  >
    <!-- Marca -->
    <div class="px-4 pt-4 pb-3">
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center w-11 h-11 overflow-hidden rounded-2xl bg-n-alpha-2 border border-n-weak shrink-0"
        >
          <img
            v-if="logoThumbnail"
            :src="logoThumbnail"
            :alt="installationName"
            class="object-contain w-9 h-9"
          />
          <span
            v-else
            class="text-base font-bold tracking-tight text-n-slate-12"
          >
            MB
          </span>
        </div>

        <div class="flex-1 min-w-0">
          <div
            class="text-[10px] font-semibold uppercase tracking-[0.18em] text-n-slate-9"
          >
            Mayara Bioni
          </div>
          <div
            class="mt-0.5 text-sm font-semibold leading-tight text-n-slate-12"
          >
            Atendimento Comercial
          </div>
        </div>

        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-xl border border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors shrink-0"
          :title="isDark ? 'Usar tema claro' : 'Usar tema escuro'"
          :aria-label="isDark ? 'Usar tema claro' : 'Usar tema escuro'"
          @click="toggleTheme"
        >
          <i
            :class="[
              isDark ? 'i-lucide-sun' : 'i-lucide-moon',
              'text-base',
            ]"
          />
        </button>
      </div>
    </div>

    <!-- Nova conversa -->
    <div class="px-3 mt-2">
      <ComposeConversation align="start">
        <template #trigger="{ isOpen }">
          <button
            type="button"
            class="mayara-new-conversation flex items-center justify-center w-full gap-2.5 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all border"
            :class="{ 'mayara-new-conversation--open': isOpen }"
          >
            <i class="text-base i-lucide-plus" />
            <span>Nova conversa</span>
          </button>
        </template>
      </ComposeConversation>
    </div>

    <!-- Busca global -->
    <div class="px-3 mt-2">
      <button
        type="button"
        class="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl border transition-all"
        :class="
          isSearchArea
            ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm dark:bg-blue-950/40 dark:border-blue-700 dark:text-blue-300'
            : 'border-blue-200 bg-blue-50/70 text-n-slate-11 hover:bg-blue-100/80 hover:border-blue-300 hover:shadow-md dark:border-blue-800/60 dark:bg-blue-950/20 dark:hover:bg-blue-950/40 dark:hover:border-blue-700 dark:hover:shadow-md'
        "
        @click="goToSearch"
      >
        <span
          class="flex items-center justify-center w-9 h-9 rounded-xl bg-blue-100 border border-blue-200 text-blue-600 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-400 shrink-0"
        >
          <i class="text-lg i-lucide-search" />
        </span>

        <div class="flex flex-col flex-1 min-w-0 text-left">
          <span class="text-sm font-semibold text-n-slate-12">
            Buscar
          </span>
          <span class="text-[10px] leading-tight text-n-slate-8">
            Paciente, conversa ou mensagem
          </span>
        </div>

        <i class="text-xs i-lucide-chevron-right text-n-slate-8" />
      </button>
    </div>

    <!-- Comercial -->
    <div class="px-3 mt-2">
      <button
        type="button"
        class="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl border transition-all"
        :class="
          isCommercialArea
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300'
            : 'border-emerald-100 bg-emerald-50/40 text-n-slate-11 hover:bg-emerald-50 hover:border-emerald-200 hover:shadow-md dark:border-emerald-900/40 dark:bg-emerald-950/10 dark:hover:bg-emerald-950/30 dark:hover:border-emerald-800 dark:hover:shadow-md'
        "
        @click="goToCommercial"
      >
        <span
          class="flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-100 border border-emerald-200 text-emerald-600 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-400 shrink-0"
        >
          <i class="text-lg i-lucide-chart-no-axes-combined" />
        </span>

        <div class="flex flex-col flex-1 min-w-0 text-left">
          <span class="text-sm font-semibold text-n-slate-12">
            Comercial
          </span>
          <span class="text-[10px] leading-tight text-n-slate-8">
            Leads, conversão e repescagem
          </span>
        </div>

        <i class="text-xs i-lucide-chevron-right text-n-slate-8" />
      </button>
    </div>

    <!-- Pacientes -->
    <div class="px-3 mt-2">
      <button
        type="button"
        class="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl border transition-all"
        :class="
          isPatientsArea
            ? 'bg-violet-50 border-violet-200 text-violet-700 shadow-sm dark:bg-violet-950/30 dark:border-violet-800 dark:text-violet-300'
            : 'border-violet-100 bg-violet-50/40 text-n-slate-11 hover:bg-violet-50 hover:border-violet-200 hover:shadow-md dark:border-violet-900/40 dark:bg-violet-950/10 dark:hover:bg-violet-950/30 dark:hover:border-violet-800 dark:hover:shadow-md'
        "
        @click="goToPatients"
      >
        <span
          class="flex items-center justify-center w-9 h-9 rounded-xl bg-violet-100 border border-violet-200 text-violet-600 dark:bg-violet-950/40 dark:border-violet-800 dark:text-violet-400 shrink-0"
        >
          <i class="text-lg i-lucide-users-round" />
        </span>

        <div class="flex flex-col flex-1 min-w-0 text-left">
          <span class="text-sm font-semibold text-n-slate-12">
            Pacientes
          </span>
          <span class="text-[10px] leading-tight text-n-slate-8">
            Cadastros e histórico
          </span>
        </div>

        <i class="text-xs i-lucide-chevron-right text-n-slate-8" />
      </button>
    </div>

    <!-- Canais -->
    <div class="px-3 mt-6">
      <div
        class="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-n-slate-8"
      >
        Canais de atendimento
      </div>

      <div class="flex flex-col gap-1">
        <button
          v-for="channel in channels"
          :key="channel.key"
          type="button"
          :disabled="!channel.available"
          class="group flex items-center w-full gap-3 px-3 py-2.5 rounded-xl transition-all"
          :class="[
            !channel.available
              ? 'opacity-50 cursor-default'
              : isChannelActive(channel)
                ? 'bg-n-alpha-2'
                : 'hover:bg-n-alpha-1',
          ]"
          @click="openChannel(channel)"
        >
          <span
            class="relative flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
            :class="{
              'bg-n-alpha-2 text-n-slate-12':
                channel.key === 'all',

              'mayara-whatsapp-icon':
                channel.key === 'whatsapp',

              'mayara-instagram-icon text-white shadow-sm':
                channel.key === 'instagram',

              'mayara-email-icon':
                channel.key === 'email',
            }"
          >
            <i :class="[channel.icon, 'text-lg']" />

            <span
              v-if="channel.available && channel.key !== 'all'"
              class="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-n-background"
            />
          </span>

          <div class="flex flex-col flex-1 min-w-0 text-left">
            <span
              class="text-sm font-semibold text-n-slate-12"
            >
              {{ channel.name }}
            </span>

            <span
              class="text-[10px] leading-tight text-n-slate-8 truncate"
            >
              {{
                channel.available
                  ? channel.description
                  : 'Canal indisponível'
              }}
            </span>
          </div>

          <i
            v-if="channel.available && channel.key !== 'all'"
            class="text-xs i-lucide-chevron-right text-n-slate-8"
          />
        </button>
      </div>
    </div>

    <!-- Administração -->
    <div v-if="isAdmin" class="px-3 mt-6">
      <div
        class="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-n-slate-8"
      >
        Sistema
      </div>

      <button
        type="button"
        class="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl transition-all"
        :class="
          isAdministrationArea
            ? 'bg-n-alpha-2 text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="goToAdministration"
      >
        <span
          class="flex items-center justify-center w-9 h-9 rounded-xl bg-n-alpha-2 border border-n-weak text-n-slate-11 shrink-0"
        >
          <i class="text-lg i-lucide-settings" />
        </span>

        <div class="flex flex-col flex-1 min-w-0 text-left">
          <span class="text-sm font-semibold text-n-slate-12">
            Administração
          </span>
          <span class="text-[10px] text-n-slate-8">
            Configurações avançadas
          </span>
        </div>

        <i class="text-xs i-lucide-chevron-right text-n-slate-8" />
      </button>
    </div>

    <div class="flex-1" />

    <!-- Preferências pessoais -->
    <div class="px-3 pb-2">
      <button
        type="button"
        class="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl transition-all"
        :class="
          isMyAccountArea
            ? 'bg-n-alpha-2 text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="goToMyAccount"
      >
        <span
          class="flex items-center justify-center w-9 h-9 rounded-xl bg-n-alpha-1 border border-n-weak text-n-slate-11 shrink-0"
        >
          <i class="text-lg i-lucide-user-round-cog" />
        </span>

        <div class="flex flex-col flex-1 min-w-0 text-left">
          <span class="text-sm font-semibold text-n-slate-12">
            Minha conta
          </span>
          <span class="text-[10px] text-n-slate-8">
            Perfil e preferências pessoais
          </span>
        </div>

        <i class="text-xs i-lucide-chevron-right text-n-slate-8" />
      </button>
    </div>

    <!-- Rodapé -->
    <div class="p-3">
      <div
        class="flex items-center gap-3 px-3.5 py-3 rounded-xl border border-n-weak bg-n-alpha-1"
      >
        <span
          class="flex items-center justify-center w-8 h-8 rounded-full bg-n-background border border-n-weak"
        >
          <i class="text-sm i-lucide-sparkles text-n-slate-11" />
        </span>

        <div class="flex flex-col min-w-0">
          <span class="text-xs font-semibold text-n-slate-12">
            Mayara Care
          </span>
          <span class="text-[10px] text-n-slate-8">
            Atendimento inteligente
          </span>
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.mayara-whatsapp-icon {
  color: #16a34a;
  background:
    radial-gradient(
      circle at 35% 30%,
      rgba(34, 197, 94, 0.18),
      rgba(34, 197, 94, 0.08) 58%,
      rgba(34, 197, 94, 0.04) 100%
    );
  border: 1px solid rgba(34, 197, 94, 0.28);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 2px 8px rgba(22, 163, 74, 0.08);
}

.mayara-email-icon {
  color: #2563eb;
  background:
    linear-gradient(
      145deg,
      rgba(59, 130, 246, 0.16),
      rgba(59, 130, 246, 0.05)
    );
  border: 1px solid rgba(59, 130, 246, 0.25);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 2px 8px rgba(37, 99, 235, 0.07);
}

.mayara-instagram-icon {
  background:
    radial-gradient(
      circle at 30% 107%,
      #fdf497 0%,
      #fdf497 5%,
      #fd5949 45%,
      #d6249f 60%,
      #285aeb 90%
    );
}

.mayara-new-conversation {
  color: #ffffff;
  background: #16a34a;
  border-color: #16a34a;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.08);
}

.mayara-new-conversation:hover {
  background: #15803d;
  border-color: #15803d;
}

.mayara-new-conversation--open {
  color: #15803d;
  background: rgb(34 197 94 / 0.12);
  border-color: rgb(34 197 94 / 0.28);
  box-shadow: none;
}

:global(.dark) .mayara-new-conversation--open {
  color: #86efac;
  background: rgb(34 197 94 / 0.14);
  border-color: rgb(34 197 94 / 0.30);
}

</style>
